import React, { useState, useEffect, useCallback } from 'react';
import {
   Calendar,
   Check,
   ArrowUpRight,
   Receipt,
   AlertCircle,
   CreditCard,
   Building2,
   FileText,
   Users,
   RefreshCw,
   Plus,
   ShoppingCart,
   Edit,
   User,
} from 'lucide-react';
import { MdChevronRight } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { useUser } from 'contexts';
import { useDispatch, useSelector } from 'react-redux';
import {
   AddOrder,
   getCustomerFormPackage,
   getDashboardCount,
   getGlobalSetting,
   getPackage,
} from '../../redux/actions/package';
import PlanCarousel from './PlanCarousel';
import WalletData from './walletData';
import { useColorMode } from '@chakra-ui/react';
import { config } from 'config/config';
import { toast } from 'react-toastify';
import { userProfile } from '../../redux/actions/user';
import EditBusinessModal from './Customer';
import { FaUserPen } from 'react-icons/fa6';

// UserDetailsPage Component with smooth animations and fixed scroll behavior
function UserDetailsPage({ isOpen, onClose }) {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const dispatch = useDispatch();
   const { user } = useUser();
   const [currentPlan, setCurrentPlan] = useState(null);
   const [showUpgrade, setShowUpgrade] = useState(false);
   const [showTransactions, setShowTransactions] = useState(false);
   const [isAnimating, setIsAnimating] = useState(false);
   const [showWalletModal, setShowWalletModal] = useState(false);
   const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
   const [creditType, setCreditType] = useState('EWAY'); // 'EWAY' or 'EINVOICE'
   const [isModalOpen, setIsModalOpen] = useState(false);
   const [creditCount, setCreditCount] = useState(100);
   const [isProcessingPayment, setIsProcessingPayment] = useState(false);
   const GlobalSetting = useSelector((state) => state?.Package?.globalSetting);
   const DashboardCount = useSelector((state) => state.Package.dashboardCount);
   const PackageData = useSelector((state) => state.Package?.list || []);
   const activeUserPackage = user?.packageData?.find((pkg) => pkg.status === true);
   const walletData = user?.walletData;
   const PackageCustomer = useSelector((state) => state?.Package.packageCustomer);
   const isAdmin = user?.isAdmin === true;
   const handleSubmit = (data) => {
      console.log('Submitted data:', data);
      // Handle form submission (API call, state update, etc.)
   };
   // Calculate rates from GlobalSetting
   const eInvoiceRate = parseFloat(GlobalSetting?.eInvoiceRatePerPcs) || 1.0;
   const eWayRate = parseFloat(GlobalSetting?.ewayRatePerPcs) || 0.5;

   // Calculate total amount based on credit type and count
   const calculateTotalAmount = () => {
      const rate = creditType === 'EINVOICE' ? eInvoiceRate : eWayRate;
      return creditCount * rate;
   };

   // Safe console log with null check
   useEffect(() => {
      if (user?.ewayTransactionData) {
         console.log('user', user.ewayTransactionData);
      }
   }, [user?.ewayTransactionData]);

   useEffect(() => {
      if (user && activeUserPackage) {
         setCurrentPlan(activeUserPackage?.pId, null);
      }
   }, [user, activeUserPackage]);

   const currentPlanData =
      user &&
      (activeUserPackage ||
         PackageData.find((p) => p.id === currentPlan && !p.isDemo) ||
         PackageData.find((p) => !p.isDemo));

   // Calculate days until expiration
   const calculateDaysUntilExpiry = (expiryDate) => {
      if (!expiryDate) return null;
      const today = new Date();
      const expiry = new Date(expiryDate);
      const diffTime = expiry - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
   };

   // Check expiry date from active package or user data
   const expiryDate =
      activeUserPackage?.expiryDate ||
      activeUserPackage?.subscriptionExpiryDate ||
      user?.subscriptionExpiryDate ||
      user?.nextBillingDate;

   const daysUntilExpiry = calculateDaysUntilExpiry(expiryDate);
   const showExpiryWarning = daysUntilExpiry !== null && daysUntilExpiry <= 30 && daysUntilExpiry > 0;

   useEffect(() => {
      dispatch(getPackage());
      dispatch(getDashboardCount());
      dispatch(getGlobalSetting());
      dispatch(getCustomerFormPackage());
   }, [dispatch]);

   // Update currentPlan when user data or packageData changes
   useEffect(() => {
      const activePackage = user?.packageData?.find((pkg) => pkg.status === true);
      if (activePackage) {
         setCurrentPlan(activePackage.pId);
      }
   }, [user, user?.packageData]);

   // Lock body scroll when any modal is open
   useEffect(() => {
      if (isOpen || showTransactions || showUpgrade || showBuyCreditsModal) {
         document.body.style.overflow = 'hidden';
      } else {
         document.body.style.overflow = 'unset';
      }

      return () => {
         document.body.style.overflow = 'unset';
      };
   }, [isOpen, showTransactions, showUpgrade, showBuyCreditsModal]);

   const handleUpgrade = (planId) => {
      setCurrentPlan(planId);
      setShowUpgrade(false);
   };

   // Trigger animation when isOpen changes
   useEffect(() => {
      if (isOpen) {
         setIsAnimating(true);
      }
   }, [isOpen]);

   // Handle close with animation
   const handleClose = useCallback(() => {
      setIsAnimating(false);
      setTimeout(() => {
         onClose();
      }, 300);
   });

   // Format date helper
   const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
   };

   useEffect(() => {
      const handleEscKey = (event) => {
         if (event.keyCode === 27) {
            // ESC key
            if (showTransactions) {
               setShowTransactions(false);
            } else if (showBuyCreditsModal) {
               setShowBuyCreditsModal(false);
            } else if (isOpen && !showUpgrade) {
               console.log('test data', isOpen, showUpgrade);
               handleClose();
            }
         }
      };

      document.addEventListener('keydown', handleEscKey);

      return () => {
         document.removeEventListener('keydown', handleEscKey);
      };
   }, [showTransactions, showUpgrade, showBuyCreditsModal, isOpen, handleClose]);

   // Get transaction type badge color
   const getTypeBadgeColor = (type) => {
      switch (type) {
         case 'EINVOICE':
            return isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700';
         case 'EWAY':
            return isDark ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700';
         default:
            return isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700';
      }
   };

   // Calculate total counts by type
   const getTransactionSummary = () => {
      if (!user) {
         return {
            EINVOICE: 0,
            EWAY: 0,
            total: 0,
         };
      }

      // Use the direct total fields from user data
      const eInvoiceTotal = user.totalEInvoiceCredit || 0;
      const eWayTotal = user.totalEwayCredit || 0;
      const transactionCount = user.ewayTransactionData?.length || 0;

      return {
         EINVOICE: eInvoiceTotal,
         EWAY: eWayTotal,
         total: transactionCount,
      };
   };

   // Handle buy credits with Razorpay integration
   const handleBuyCredits = async () => {
      const totalAmount = calculateTotalAmount();

      if (totalAmount <= 0) {
         toast.error('Please select valid credit count');
         return;
      }

      setIsProcessingPayment(true);

      try {
         // Create order first
         const addOrderResult = await dispatch(
            AddOrder({
               amount: totalAmount,
            }),
         );

         if (addOrderResult && addOrderResult.code === 1) {
            const orderId = addOrderResult.data?.orderId || addOrderResult.orderId;

            if (!orderId) {
               throw new Error('Order ID not received from server');
            }

            // Prepare notes for credits purchase
            const notes = {
               txnType: 'EWAY',
               count: creditCount,
               type: creditType,
               ratePerPcs: creditType === 'EINVOICE' ? eInvoiceRate : eWayRate,
               code: user.code,
            };

            // Check if Razorpay is available
            if (!window.Razorpay) {
               throw new Error('Razorpay SDK not loaded');
            }

            const options = {
               key: config.RZRPAYKEY,
               amount: Math.round(totalAmount * 100), // Convert to paise
               currency: 'INR',
               order_id: orderId,
               name: 'Zeronine Tax',
               description: `Continue payment to purchase ${creditType} credits`,
               handler: async function (response) {
                  // Payment Success
                  console.log('✅ Credit Purchase Payment Successful!');
                  toast.success('Payment Successful! Processing your credits...');

                  try {
                     // Call getProfile to refresh user data and credits
                     console.log('🔄 Fetching updated profile...');
                     await dispatch(userProfile());
                     console.log('✅ Profile updated successfully');

                     // Show success message
                     toast.success(`${creditCount} ${creditType} credits added successfully!`);

                     // Close the modal after successful payment
                     setShowBuyCreditsModal(false);

                     // Reset form
                     setCreditCount(100);
                     setCreditType('EWAY');
                  } catch (error) {
                     console.error('Profile update error:', error);
                     toast.error('Payment successful but profile update failed. Please contact support.');
                  } finally {
                     setIsProcessingPayment(false);
                  }
               },
               prefill: {
                  name: user?.name || '',
                  email: user?.email || '',
                  contact: user?.phone || '',
               },
               notes: notes,
               theme: {
                  color: '#3399cc',
               },
               modal: {
                  ondismiss: function () {
                     console.log('⚠️ Payment Cancelled by User');
                     toast.warning('Payment cancelled');
                     setIsProcessingPayment(false);
                  },
               },
            };

            const rzp = new window.Razorpay(options);

            rzp.on('payment.failed', function (response) {
               console.error('❌ Payment Failed!');
               console.error('Error:', response.error);
               console.error('Error Code:', response.error.code);
               console.error('Error Description:', response.error.description);
               console.error('Error Source:', response.error.source);
               console.error('Error Step:', response.error.step);
               console.error('Error Reason:', response.error.reason);

               toast.error(response.error.description || 'Payment failed. Please try again or contact support.');

               setIsProcessingPayment(false);
            });

            // OPEN RAZORPAY
            rzp.open();
         } else {
            throw new Error('Failed to create order. Please try again.');
         }
      } catch (error) {
         console.error('Razorpay payment initialization error:', error);
         toast.error(error.message || 'Failed to initialize payment. Please try again.');
         setIsProcessingPayment(false);
      }
   };

   if (!isOpen) return null;

   const transactionSummary = getTransactionSummary();

   const formatQuota = (value) => {
      if (value === -100) return 'Unlimited';
      if (value === null || value === undefined) return '0';
      return value.toLocaleString();
   };

   // Helper function to check if unlimited
   const isUnlimited = (value) => value === -100;

   // Helper function to calculate remaining
   const calculateRemaining = (total, used) => {
      if (isUnlimited(total)) return '∞';
      const remaining = total - (used || 0);
      return remaining >= 0 ? remaining.toLocaleString() : '0';
   };

   // Helper function to calculate progress percentage
   const calculateProgress = (used, total) => {
      if (isUnlimited(total)) return 0; // No progress bar for unlimited
      return Math.min(((used || 0) / total) * 100, 100);
   };

   const isPlanExpired = () => {
      if (!expiryDate) return false;
      const today = new Date();
      const expiry = new Date(expiryDate);
      return expiry < today;
   };

   // Add this constant after calculating daysUntilExpiry
   const isExpired = user?.isExpired || isPlanExpired();

   return (
      <div className="fixed inset-0 z-[999]">
         {/* Backdrop with fade animation */}
         <div
            className={`absolute inset-0 ${
               isDark ? 'bg-black/70' : 'bg-black/50'
            } backdrop-blur-sm transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
            onClick={handleClose}
         />
         {/* Drawer Content with slide animation - FIXED HEIGHT AND OVERFLOW */}
         <div
            className={`absolute right-0 top-0 h-full w-full max-w-7xl ${
               isDark ? 'bg-slate-900' : 'bg-slate-50'
            } shadow-2xl transition-transform duration-300 ease-out flex flex-col ${
               isAnimating ? 'translate-x-0' : 'translate-x-full'
            }`}>
            {/* Close Button - FIXED POSITION */}
            <button
               onClick={handleClose}
               className={`absolute top-4 left-4 z-10 w-10 h-10 ${
                  isDark
                     ? 'bg-slate-800/90 text-slate-300 hover:bg-slate-700'
                     : 'bg-white/90 text-slate-700 hover:bg-white'
               } backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-all duration-200`}>
               <MdChevronRight className="w-6 h-6 rotate-180" />
            </button>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
               <div className="max-w-7xl mx-auto px-6 py-8">
                  {/* Expiry Warning Banner */}
                  {showExpiryWarning && !isExpired && (
                     <div className="mb-6 mt-5 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-6 shadow-lg">
                        <div className="flex items-start gap-4">
                           <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                              <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                 <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                 />
                              </svg>
                           </div>
                           <div className="flex-1">
                              <h3 className="text-xl font-bold text-white mb-2">Your Plan Expires Soon!</h3>
                              <p className="text-white/90 mb-4">
                                 Your current subscription will expire on {formatDate(expiryDate)} ({daysUntilExpiry}{' '}
                                 days remaining). Renew now to continue enjoying all premium features without
                                 interruption.
                              </p>
                              <div className="flex items-center gap-3">
                                 <button
                                    onClick={() => setShowUpgrade(true)}
                                    className="px-5 py-2.5 bg-white hover:bg-slate-100 text-orange-600 font-semibold rounded-lg transition-all duration-200 hover:scale-105 shadow-lg">
                                    Renew Now
                                 </button>
                                 <button className="px-5 py-2.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-medium rounded-lg transition-colors">
                                    Remind Me Later
                                 </button>
                              </div>
                           </div>
                        </div>
                     </div>
                  )}

                  {/* Header */}
                  <div className="mb-8 mt-5 flex items-center justify-between">
                     <div>
                        <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>
                           Subscription Details
                        </h1>
                        <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>
                           Manage your subscription preferences
                        </p>
                     </div>
                     {isAdmin && (
                        <button
                           onClick={() => setIsModalOpen(true)}
                           className={`p-2 rounded-md transition-colors ${
                              isDark
                                 ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                                 : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                           }`}
                           title="Edit subscription">
                           <FaUserPen size={28} />
                        </button>
                     )}
                  </div>

                  <div className="grid lg:grid-cols-3 gap-6">
                     {/* Left Column - Profile */}
                     <div className="lg:col-span-2 space-y-6">
                        {/* Current Plan */}
                        {currentPlanData && !isExpired && (
                           <>
                              {/* Header Card */}
                              <div
                                 className={`${
                                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                 } rounded-xl shadow-sm border p-6`}>
                                 <div className="flex items-start justify-between mb-6">
                                    <div>
                                       <div className="flex items-center gap-3 mb-2">
                                          <h3
                                             className={`text-2xl font-bold ${
                                                isDark ? 'text-white' : 'text-slate-900'
                                             }`}>
                                             Current Subscription
                                          </h3>
                                       </div>
                                       <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>
                                          You're on the{' '}
                                          <span
                                             className={`${
                                                isDark ? 'text-blue-400' : 'text-blue-600'
                                             } text-lg font-semibold`}>
                                             {currentPlanData.packageName}
                                          </span>{' '}
                                          plan
                                       </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                       <button
                                          onClick={() => setShowWalletModal(true)}
                                          className={`px-5 py-2.5 ${
                                             isDark
                                                ? 'bg-green-900/50 text-green-300 hover:bg-green-800'
                                                : 'bg-green-100 hover:bg-green-200 text-green-700'
                                          } rounded-lg font-medium transition-colors flex items-center gap-2`}>
                                          <CreditCard className="w-4 h-4" />
                                          Wallet
                                       </button>
                                       <button
                                          onClick={() => setShowTransactions(true)}
                                          className={`px-5 py-2.5 ${
                                             isDark
                                                ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                                          } rounded-lg font-medium transition-colors flex items-center gap-2`}>
                                          <Receipt className="w-4 h-4" />
                                          Transactions
                                       </button>
                                       <button
                                          onClick={() => setShowUpgrade(true)}
                                          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2">
                                          Upgrade Plan
                                          <ArrowUpRight className="w-4 h-4" />
                                       </button>
                                    </div>
                                 </div>

                                 {/* Pricing and Payment Info */}
                                 <div
                                    className={`grid md:grid-cols-3 gap-6 pb-6 border-b ${
                                       isDark ? 'border-slate-700' : 'border-slate-200'
                                    }`}>
                                    <div>
                                       <div className="flex items-baseline gap-2 mb-1">
                                          <span
                                             className={`text-3xl font-bold ${
                                                isDark ? 'text-white' : 'text-slate-900'
                                             }`}>
                                             ₹{currentPlanData.amount?.toLocaleString()}
                                          </span>
                                       </div>
                                    </div>

                                    <div className="flex items-center gap-3"></div>

                                    <div className="flex items-center gap-3">
                                       <div
                                          className={`w-10 h-10 rounded-lg ${
                                             isDark ? 'bg-green-900/50' : 'bg-green-100'
                                          } flex items-center justify-center`}>
                                          <Calendar
                                             className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`}
                                          />
                                       </div>
                                       <div>
                                          <p
                                             className={`text-sm font-medium ${
                                                isDark ? 'text-white' : 'text-slate-900'
                                             }`}>
                                             {currentPlanData.expireInDays} days left
                                          </p>
                                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                             Until renewal
                                          </p>
                                       </div>
                                    </div>
                                 </div>

                                 {/* Subscription Period */}
                                 <div className={`mt-6 p-4 ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'} rounded-lg`}>
                                    <div className="flex items-center justify-between text-sm">
                                       <div>
                                          <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'} mb-1`}>
                                             Start Date
                                          </p>
                                          <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                             {new Date(currentPlanData.startDate).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric',
                                             })}
                                          </p>
                                       </div>
                                       <div
                                          className={`flex-1 mx-4 border-t-2 border-dashed ${
                                             isDark ? 'border-slate-600' : 'border-slate-300'
                                          }`}></div>
                                       <div className="text-right">
                                          <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'} mb-1`}>
                                             End Date
                                          </p>
                                          <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                             {new Date(currentPlanData.endDate).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric',
                                             })}
                                          </p>
                                       </div>
                                    </div>
                                 </div>
                              </div>

                              {/* Usage Limits */}
                              <div
                                 className={`${
                                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                 } rounded-xl shadow-sm border p-4`}>
                                 <h4 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'} mb-6`}>
                                    Usage Limits & Quotas
                                 </h4>
                                 <div className="grid md:grid-cols-5 gap-4">
                                    {/* Companies */}
                                    <div
                                       className={`p-4 ${
                                          isDark
                                             ? 'bg-slate-700/50 border-slate-600'
                                             : 'bg-gradient-to-br from-blue-50 to-slate-50 border-slate-200'
                                       } rounded-lg border`}>
                                       <div className="flex items-center justify-between mb-3">
                                          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                                             <Building2 className="w-4 h-4 text-white" />
                                          </div>
                                          <span
                                             className={`text-xs font-semibold ${
                                                isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-600'
                                             } px-2 py-1 rounded-full`}>
                                             {calculateRemaining(
                                                currentPlanData.pack_noOfCompanies,
                                                DashboardCount?.companies,
                                             )}{' '}
                                             Left
                                          </span>
                                       </div>
                                       <div className="space-y-2">
                                          <div className="flex items-baseline gap-1">
                                             <span
                                                className={`text-2xl font-bold ${
                                                   isDark ? 'text-white' : 'text-slate-900'
                                                }`}>
                                                {(DashboardCount?.companies || 0).toLocaleString()}
                                             </span>
                                             <span
                                                className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                                / {formatQuota(currentPlanData.pack_noOfCompanies)}
                                             </span>
                                          </div>
                                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                             Companies Used
                                          </p>
                                          {/* Progress Bar - Hidden for unlimited */}
                                          {!isUnlimited(currentPlanData.pack_noOfCompanies) && (
                                             <div
                                                className={`w-full ${
                                                   isDark ? 'bg-slate-600' : 'bg-slate-200'
                                                } rounded-full h-1.5`}>
                                                <div
                                                   className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                                   style={{
                                                      width: `${calculateProgress(
                                                         DashboardCount?.companies,
                                                         currentPlanData.pack_noOfCompanies,
                                                      )}%`,
                                                   }}
                                                />
                                             </div>
                                          )}
                                          {isUnlimited(currentPlanData.pack_noOfCompanies) && (
                                             <div
                                                className={`text-xs font-semibold ${
                                                   isDark ? 'text-blue-400' : 'text-blue-600'
                                                }`}>
                                                Unlimited Plan
                                             </div>
                                          )}
                                       </div>
                                    </div>

                                    {/* E-Ways */}
                                    <div
                                       className={`p-4 ${
                                          isDark
                                             ? 'bg-slate-700/50 border-slate-600'
                                             : 'bg-gradient-to-br from-purple-50 to-slate-50 border-slate-200'
                                       } rounded-lg border`}>
                                       <div className="flex items-center justify-between mb-3">
                                          <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center">
                                             <FileText className="w-4 h-4 text-white" />
                                          </div>
                                          <span
                                             className={`text-xs font-semibold ${
                                                isDark
                                                   ? 'bg-purple-900/50 text-purple-300'
                                                   : 'bg-purple-100 text-purple-600'
                                             } px-2 py-1 rounded-full`}>
                                             {calculateRemaining(
                                                currentPlanData.pack_noOfEways,
                                                DashboardCount?.ewayBills,
                                             )}{' '}
                                             Left
                                          </span>
                                       </div>
                                       <div className="space-y-2">
                                          <div className="flex items-baseline gap-1">
                                             <span
                                                className={`text-2xl font-bold ${
                                                   isDark ? 'text-white' : 'text-slate-900'
                                                }`}>
                                                {(DashboardCount?.ewayBills || 0).toLocaleString()}
                                             </span>
                                             <span
                                                className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                                / {formatQuota(currentPlanData.pack_noOfEways)}
                                             </span>
                                          </div>
                                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                             E-Ways Used
                                          </p>
                                          {!isUnlimited(currentPlanData.pack_noOfEways) && (
                                             <div
                                                className={`w-full ${
                                                   isDark ? 'bg-slate-600' : 'bg-slate-200'
                                                } rounded-full h-1.5`}>
                                                <div
                                                   className="bg-purple-600 h-1.5 rounded-full transition-all duration-300"
                                                   style={{
                                                      width: `${calculateProgress(
                                                         DashboardCount?.ewayBills,
                                                         currentPlanData.pack_noOfEways,
                                                      )}%`,
                                                   }}
                                                />
                                             </div>
                                          )}
                                          {isUnlimited(currentPlanData.pack_noOfEways) && (
                                             <div
                                                className={`text-xs font-semibold ${
                                                   isDark ? 'text-purple-400' : 'text-purple-600'
                                                }`}>
                                                Unlimited Plan
                                             </div>
                                          )}
                                       </div>
                                    </div>

                                    {/* E-Invoices */}
                                    <div
                                       className={`p-4 ${
                                          isDark
                                             ? 'bg-slate-700/50 border-slate-600'
                                             : 'bg-gradient-to-br from-green-50 to-slate-50 border-slate-200'
                                       } rounded-lg border`}>
                                       <div className="flex items-center justify-between mb-3">
                                          <div className="w-8 h-8 rounded-lg bg-green-600 flex items-center justify-center">
                                             <FileText className="w-4 h-4 text-white" />
                                          </div>
                                          <span
                                             className={`text-xs font-semibold ${
                                                isDark
                                                   ? 'bg-green-900/50 text-green-300'
                                                   : 'bg-green-100 text-green-600'
                                             } px-2 py-1 rounded-full`}>
                                             {calculateRemaining(
                                                currentPlanData.pack_noOfEInvoice,
                                                DashboardCount?.eInvoices,
                                             )}{' '}
                                             Left
                                          </span>
                                       </div>
                                       <div className="space-y-2">
                                          <div className="flex items-baseline gap-1">
                                             <span
                                                className={`text-2xl font-bold ${
                                                   isDark ? 'text-white' : 'text-slate-900'
                                                }`}>
                                                {(DashboardCount?.eInvoices || 0).toLocaleString()}
                                             </span>
                                             <span
                                                className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                                / {formatQuota(currentPlanData.pack_noOfEInvoice)}
                                             </span>
                                          </div>
                                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                             E-Invoices Used
                                          </p>
                                          {!isUnlimited(currentPlanData.pack_noOfEInvoice) && (
                                             <div
                                                className={`w-full ${
                                                   isDark ? 'bg-slate-600' : 'bg-slate-200'
                                                } rounded-full h-1.5`}>
                                                <div
                                                   className="bg-green-600 h-1.5 rounded-full transition-all duration-300"
                                                   style={{
                                                      width: `${calculateProgress(
                                                         DashboardCount?.eInvoices,
                                                         currentPlanData.pack_noOfEInvoice,
                                                      )}%`,
                                                   }}
                                                />
                                             </div>
                                          )}
                                          {isUnlimited(currentPlanData.pack_noOfEInvoice) && (
                                             <div
                                                className={`text-xs font-semibold ${
                                                   isDark ? 'text-green-400' : 'text-green-600'
                                                }`}>
                                                Unlimited Plan
                                             </div>
                                          )}
                                       </div>
                                    </div>

                                    {/* Users */}
                                    <div
                                       className={`p-4 ${
                                          isDark
                                             ? 'bg-slate-700/50 border-slate-600'
                                             : 'bg-gradient-to-br from-orange-50 to-slate-50 border-slate-200'
                                       } rounded-lg border`}>
                                       <div className="flex items-center justify-between mb-3">
                                          <div className="w-8 h-8 rounded-lg bg-orange-600 flex items-center justify-center">
                                             <Users className="w-4 h-4 text-white" />
                                          </div>
                                          <span
                                             className={`text-xs font-semibold ${
                                                isDark
                                                   ? 'bg-orange-900/50 text-orange-300'
                                                   : 'bg-orange-100 text-orange-600'
                                             } px-2 py-1 rounded-full`}>
                                             {calculateRemaining(currentPlanData.pack_noOfUser, DashboardCount?.users)}{' '}
                                             Left
                                          </span>
                                       </div>
                                       <div className="space-y-2">
                                          <div className="flex items-baseline gap-1">
                                             <span
                                                className={`text-2xl font-bold ${
                                                   isDark ? 'text-white' : 'text-slate-900'
                                                }`}>
                                                {(DashboardCount?.users || 0).toLocaleString()}
                                             </span>
                                             <span
                                                className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                                / {formatQuota(currentPlanData.pack_noOfUser)}
                                             </span>
                                          </div>
                                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                             Users Created
                                          </p>
                                          {!isUnlimited(currentPlanData.pack_noOfUser) && (
                                             <div
                                                className={`w-full ${
                                                   isDark ? 'bg-slate-600' : 'bg-slate-200'
                                                } rounded-full h-1.5`}>
                                                <div
                                                   className="bg-orange-600 h-1.5 rounded-full transition-all duration-300"
                                                   style={{
                                                      width: `${calculateProgress(
                                                         DashboardCount?.users,
                                                         currentPlanData.pack_noOfUser,
                                                      )}%`,
                                                   }}
                                                />
                                             </div>
                                          )}
                                          {isUnlimited(currentPlanData.pack_noOfUser) && (
                                             <div
                                                className={`text-xs font-semibold ${
                                                   isDark ? 'text-orange-400' : 'text-orange-600'
                                                }`}>
                                                Unlimited Plan
                                             </div>
                                          )}
                                       </div>
                                    </div>

                                    {/* WhatsApp Messages */}
                                    <div
                                       className={`p-4 ${
                                          isDark
                                             ? 'bg-slate-700/50 border-slate-600'
                                             : 'bg-gradient-to-br from-cyan-50 to-slate-50 border-slate-200'
                                       } rounded-lg border`}>
                                       <div className="flex items-center justify-between mb-3">
                                          <div className="w-8 h-8 rounded-lg bg-cyan-600 flex items-center justify-center">
                                             <RefreshCw className="w-4 h-4 text-white" />
                                          </div>
                                          <span
                                             className={`text-xs font-semibold ${
                                                isDark ? 'bg-cyan-900/50 text-cyan-300' : 'bg-cyan-100 text-cyan-600'
                                             } px-2 py-1 rounded-full`}>
                                             {calculateRemaining(currentPlanData.pack_noOfWp, DashboardCount?.wpCount)}{' '}
                                             Left
                                          </span>
                                       </div>
                                       <div className="space-y-2">
                                          <div className="flex items-baseline gap-1">
                                             <span
                                                className={`text-2xl font-bold ${
                                                   isDark ? 'text-white' : 'text-slate-900'
                                                }`}>
                                                {(DashboardCount?.wpCount || 0).toLocaleString()}
                                             </span>
                                             <span
                                                className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                                / {formatQuota(currentPlanData.pack_noOfWp)}
                                             </span>
                                          </div>
                                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                             Messages Sent
                                          </p>
                                          {!isUnlimited(currentPlanData.pack_noOfWp) && (
                                             <div
                                                className={`w-full ${
                                                   isDark ? 'bg-slate-600' : 'bg-slate-200'
                                                } rounded-full h-1.5`}>
                                                <div
                                                   className="bg-cyan-600 h-1.5 rounded-full transition-all duration-300"
                                                   style={{
                                                      width: `${calculateProgress(
                                                         DashboardCount?.wpCount,
                                                         currentPlanData.pack_noOfWp,
                                                      )}%`,
                                                   }}
                                                />
                                             </div>
                                          )}
                                          {isUnlimited(currentPlanData.pack_noOfWp) && (
                                             <div
                                                className={`text-xs font-semibold ${
                                                   isDark ? 'text-cyan-400' : 'text-cyan-600'
                                                }`}>
                                                Unlimited Plan
                                             </div>
                                          )}
                                       </div>
                                    </div>
                                 </div>
                              </div>

                              {/* Features & Modules */}
                              <div
                                 className={`${
                                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                 } rounded-xl shadow-sm border p-4`}>
                                 <h4 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'} mb-6`}>
                                    Included Features & Modules
                                 </h4>
                                 <div className="grid md:grid-cols-3 gap-3">
                                    {/* Filter and map only features that are true */}
                                    {[
                                       { key: 'isMobileApp', label: 'Mobile App Access' },
                                       { key: 'isCrm', label: 'CRM Module' },
                                       { key: 'isToDo', label: 'To-Do Management' },
                                       { key: 'isDailyWp', label: 'Daily WhatsApp' },
                                       { key: 'isDashboardGst', label: 'GST Dashboard' },
                                       { key: 'is2a2b', label: '2A/2B Reports' },
                                       { key: 'is2stepVerify', label: 'Two-Step Verification' },
                                       { key: 'sales_challan', label: 'Sales Challan' },
                                       { key: 'sales_invoice', label: 'Sales Invoice' },
                                       { key: 'purchase_challan', label: 'Purchase Challan' },
                                       { key: 'purchase_invoice', label: 'Purchase Invoice' },
                                       { key: 'sales_return', label: 'Sales Return' },
                                       { key: 'purchase_return', label: 'Purchase Return' },
                                       { key: 'issue', label: 'Issue Management' },
                                       { key: 'receive', label: 'Receive Management' },
                                       { key: 'cash', label: 'Cash Management' },
                                       { key: 'bank', label: 'Bank Management' },
                                       { key: 'jv', label: 'Journal Voucher' },
                                       { key: 'stock_transfer', label: 'Stock Transfer' },
                                       { key: 'share_company', label: 'Share Company' },
                                       { key: 'company_wise_master', label: 'Company-wise Master' },
                                       { key: 'delete_unused_master', label: 'Delete Unused Master' },
                                       { key: 'aamne_saamne_sale_purchase', label: 'Aamne Saamne Sale/Purchase' },
                                       { key: 'multiple_eway_bill_generate', label: 'Multiple E-way Bill' },
                                       { key: 'print_barcode', label: 'Print Barcode' },
                                       { key: 'sales_order', label: 'Sales Order' },
                                       { key: 'purchase_order', label: 'Purchase Order' },
                                       { key: 'bank_import', label: 'Bank Import' },
                                       { key: 'bulk_update_suspense_account', label: 'Bulk Update Suspense Account' },
                                    ]
                                       .filter(({ key }) => currentPlanData[key] === true)
                                       .map(({ key, label }) => (
                                          <div
                                             key={key}
                                             className={`flex items-center gap-3 p-3 rounded-lg ${
                                                isDark ? 'bg-slate-700/50' : 'bg-slate-50'
                                             }`}>
                                             <div
                                                className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                   isDark ? 'bg-green-900/50' : 'bg-green-100'
                                                }`}>
                                                <Check
                                                   className={`w-3 h-3 ${isDark ? 'text-green-400' : 'text-green-600'}`}
                                                />
                                             </div>
                                             <span
                                                className={`text-sm font-medium ${
                                                   isDark ? 'text-slate-300' : 'text-slate-700'
                                                }`}>
                                                {label}
                                             </span>
                                          </div>
                                       ))}

                                    {/* Show message if no features are available */}
                                    {[
                                       'isMobileApp',
                                       'isCrm',
                                       'isToDo',
                                       'isDailyWp',
                                       'isDashboardGst',
                                       'is2a2b',
                                       'is2stepVerify',
                                       'sales_challan',
                                       'sales_invoice',
                                       'purchase_challan',
                                       'purchase_invoice',
                                       'sales_return',
                                       'purchase_return',
                                       'issue',
                                       'receive',
                                       'cash',
                                       'bank',
                                       'jv',
                                       'stock_transfer',
                                       'share_company',
                                       'company_wise_master',
                                       'delete_unused_master',
                                       'aamne_saamne_sale_purchase',
                                       'multiple_eway_bill_generate',
                                       'print_barcode',
                                       'sales_order',
                                       'purchase_order',
                                       'bank_import',
                                       'bulk_update_suspense_account',
                                    ].every((key) => currentPlanData[key] !== true) && (
                                       <div className="col-span-full text-center py-8">
                                          <div
                                             className={`w-12 h-12 ${
                                                isDark ? 'bg-slate-700' : 'bg-slate-100'
                                             } rounded-full flex items-center justify-center mx-auto mb-3`}>
                                             <AlertCircle
                                                className={`w-6 h-6 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                                             />
                                          </div>
                                          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                                             No additional features included in this plan
                                          </p>
                                       </div>
                                    )}
                                 </div>
                              </div>

                              {/* Referral Program */}
                              {currentPlanData.referralAmt > 0 && (
                                 <div className="bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl shadow-lg p-8 text-white">
                                    <div className="flex items-start justify-between">
                                       <div>
                                          <h4 className="text-xl font-bold mb-2">Referral Program</h4>
                                          <p className="text-white/90 mb-4">Earn rewards by referring new customers</p>
                                          <div className="flex items-center gap-6">
                                             <div>
                                                <p className="text-3xl font-bold">₹{currentPlanData.referralAmt}</p>
                                                <p className="text-sm text-white/80">Per referral</p>
                                             </div>
                                             <div className="w-px h-12 bg-white/30"></div>
                                             <div>
                                                <p className="text-3xl font-bold">{currentPlanData.referralPer}%</p>
                                                <p className="text-sm text-white/80">Commission rate</p>
                                             </div>
                                          </div>
                                       </div>
                                       <button className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                                          Share Referral Link
                                       </button>
                                    </div>
                                 </div>
                              )}
                           </>
                        )}
                        {isExpired && (
                           <div className="mb-6 mt-5 bg-gradient-to-r from-red-500 to-rose-600 rounded-xl p-6 shadow-lg border border-red-300">
                              <div className="flex items-start gap-4">
                                 <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                                    <svg
                                       className="w-7 h-7 text-white"
                                       fill="none"
                                       viewBox="0 0 24 24"
                                       stroke="currentColor">
                                       <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                       />
                                    </svg>
                                 </div>
                                 <div className="flex-1">
                                    <h3 className="text-xl font-bold text-white mb-2">Your Plan Has Expired!</h3>
                                    <p className="text-white/90 mb-4">
                                       Your subscription expired on {formatDate(expiryDate)}. Renew now to restore
                                       access to all premium features and continue using the platform without
                                       interruptions.
                                    </p>
                                    <div className="flex items-center gap-3">
                                       <button
                                          onClick={() => setShowUpgrade(true)}
                                          className="px-5 py-2.5 bg-white hover:bg-slate-100 text-red-600 font-semibold rounded-lg transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl">
                                          Renew Now
                                       </button>
                                       <a href="/helpsupport" className={`hover:no-underline "text-red-400"`}>
                                          <button className="px-5 py-2.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-medium rounded-lg transition-colors border border-white/30">
                                             Contact support
                                          </button>
                                       </a>
                                    </div>
                                 </div>
                              </div>
                           </div>
                        )}
                     </div>

                     {/* Right Column - Quick Info */}
                     {!isExpired && (
                        <div className="space-y-6">
                           <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-sm p-6 text-white">
                              <h3 className="text-lg font-bold mb-4">Need Help?</h3>
                              <p className="text-sm mb-4">Our support team is here to assist you with any questions.</p>
                              <Link to={'/helpsupport'}>
                                 <button className="w-full px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg font-semibold transition-colors">
                                    Contact Support
                                 </button>
                              </Link>
                           </div>
                        </div>
                     )}
                  </div>
               </div>
            </div>
         </div>

         {/* Transaction History Modal - SEPARATE FIXED LAYER */}
         {showTransactions && (
            <div
               className={`fixed inset-0 ${
                  isDark ? 'bg-slate-900/80' : 'bg-slate-900/50'
               } backdrop-blur-sm flex items-center justify-center p-4 z-[1000]`}>
               <div
                  className={`${
                     isDark ? 'bg-slate-800' : 'bg-white'
                  } rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col`}>
                  <div
                     className={`sticky top-0 ${
                        isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                     } border-b p-6 flex items-center justify-between z-10 flex-shrink-0`}>
                     <div>
                        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                           Transaction History
                        </h2>
                        <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'} mt-1`}>
                           View all your eWay and eInvoice transactions
                        </p>
                     </div>
                     <div className="flex items-center gap-3">
                        {/* Buy Credits Button */}
                        <button
                           onClick={() => setShowBuyCreditsModal(true)}
                           className="px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2">
                           <Plus className="w-4 h-4" />
                           Buy Credits
                        </button>
                        <button
                           onClick={() => setShowTransactions(false)}
                           className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                              isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-400'
                           }`}>
                           <span className="text-2xl">×</span>
                        </button>
                     </div>
                  </div>

                  <div className="p-6 overflow-y-auto flex-1">
                     {/* Transaction List */}
                     {user?.ewayTransactionData && user.ewayTransactionData.length > 0 ? (
                        <div className="space-y-3">
                           {user.ewayTransactionData.map((transaction) => (
                              <div
                                 key={transaction.id}
                                 className={`${
                                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                 } border rounded-xl p-5 hover:shadow-md transition-shadow`}>
                                 <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                       <div className="flex items-center gap-3 mb-3">
                                          <span
                                             className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeBadgeColor(
                                                transaction.type,
                                             )}`}>
                                             {transaction.type}
                                          </span>
                                          <span
                                             className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                                transaction.status
                                                   ? isDark
                                                      ? 'bg-green-900/50 text-green-300'
                                                      : 'bg-green-100 text-green-700'
                                                   : isDark
                                                   ? 'bg-red-900/50 text-red-300'
                                                   : 'bg-red-100 text-red-700'
                                             }`}>
                                             {transaction.status ? 'Active' : 'Inactive'}
                                          </span>
                                       </div>

                                       <div className="grid md:grid-cols-4 gap-4">
                                          <div>
                                             <p
                                                className={`text-xs ${
                                                   isDark ? 'text-slate-400' : 'text-slate-500'
                                                } mb-1`}>
                                                Count
                                             </p>
                                             <p
                                                className={`text-sm font-semibold ${
                                                   isDark ? 'text-white' : 'text-slate-900'
                                                }`}>
                                                {transaction.count}
                                             </p>
                                          </div>
                                          <div>
                                             <p
                                                className={`text-xs ${
                                                   isDark ? 'text-slate-400' : 'text-slate-500'
                                                } mb-1`}>
                                                Package Name
                                             </p>
                                             <p
                                                className={`text-sm font-semibold ${
                                                   isDark ? 'text-white' : 'text-slate-900'
                                                }`}>
                                                {transaction.packageName || '-'}
                                             </p>
                                          </div>
                                          <div>
                                             <p
                                                className={`text-xs ${
                                                   isDark ? 'text-slate-400' : 'text-slate-500'
                                                } mb-1`}>
                                                Created Date
                                             </p>
                                             <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-900'}`}>
                                                {formatDate(transaction.createdAt)}
                                             </p>
                                          </div>
                                       </div>

                                       {transaction.amt && (
                                          <div
                                             className={`mt-3 pt-3 border-t ${
                                                isDark ? 'border-slate-700' : 'border-slate-100'
                                             }`}>
                                             <div className="flex items-center gap-4">
                                                <div>
                                                   <p
                                                      className={`text-xs ${
                                                         isDark ? 'text-slate-400' : 'text-slate-500'
                                                      } mb-1`}>
                                                      Amount
                                                   </p>
                                                   <p
                                                      className={`text-sm font-semibold ${
                                                         isDark ? 'text-white' : 'text-slate-900'
                                                      }`}>
                                                      {transaction.amt}
                                                   </p>
                                                </div>
                                                {transaction.pgMethod && (
                                                   <div>
                                                      <p
                                                         className={`text-xs ${
                                                            isDark ? 'text-slate-400' : 'text-slate-500'
                                                         } mb-1`}>
                                                         Payment Method
                                                      </p>
                                                      <p
                                                         className={`text-sm ${
                                                            isDark ? 'text-slate-300' : 'text-slate-900'
                                                         }`}>
                                                         {transaction.pgMethod}
                                                      </p>
                                                   </div>
                                                )}
                                             </div>
                                          </div>
                                       )}
                                    </div>
                                 </div>
                              </div>
                           ))}
                        </div>
                     ) : (
                        <div className="text-center py-12">
                           <div
                              className={`w-16 h-16 ${
                                 isDark ? 'bg-slate-700' : 'bg-slate-100'
                              } rounded-full flex items-center justify-center mx-auto mb-4`}>
                              <Receipt className={`w-8 h-8 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                           </div>
                           <p className={`${isDark ? 'text-slate-300' : 'text-slate-500'} text-lg font-medium mb-2`}>
                              No Transactions Found
                           </p>
                           <p className={isDark ? 'text-slate-500' : 'text-slate-400'}>
                              You don't have any transaction history yet.
                           </p>
                           <button
                              onClick={() => setShowBuyCreditsModal(true)}
                              className="mt-4 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto">
                              <ShoppingCart className="w-4 h-4" />
                              Buy Your First Credits
                           </button>
                        </div>
                     )}
                  </div>
               </div>
            </div>
         )}

         {/* Buy Credits Modal */}
         {showBuyCreditsModal && (
            <div
               className={`fixed inset-0 ${
                  isDark ? 'bg-slate-900/80' : 'bg-slate-900/50'
               } backdrop-blur-sm flex items-center justify-center p-4 z-[1001]`}>
               <div
                  className={`${
                     isDark ? 'bg-slate-800' : 'bg-white'
                  } rounded-2xl max-w-md w-full shadow-2xl animate-scaleIn`}>
                  <div
                     className={`sticky top-0 ${
                        isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                     } border-b  rounded-t-2xl p-6 flex items-center justify-between`}>
                     <div>
                        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                           Buy Credits
                        </h2>
                        <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'} mt-1`}>
                           Purchase additional eWay or eInvoice credits
                        </p>
                     </div>
                     <button
                        onClick={() => setShowBuyCreditsModal(false)}
                        disabled={isProcessingPayment}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                           isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-400'
                        } ${isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <span className="text-2xl">×</span>
                     </button>
                  </div>

                  <div className="p-6">
                     {/* Credit Type Selection */}
                     <div className="mb-6">
                        <label
                           className={`block text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                           Select Credit Type
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                           <button
                              onClick={() => !isProcessingPayment && setCreditType('EWAY')}
                              disabled={isProcessingPayment}
                              className={`p-4 rounded-lg border-2 transition-all ${
                                 creditType === 'EWAY'
                                    ? isDark
                                       ? 'border-purple-500 bg-purple-900/20'
                                       : 'border-purple-500 bg-purple-50'
                                    : isDark
                                    ? 'border-slate-600 bg-slate-700/50 hover:border-slate-500'
                                    : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                              } ${isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''}`}>
                              <div className="text-center">
                                 <div
                                    className={`w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center mx-auto mb-2`}>
                                    <FileText className="w-4 h-4 text-white" />
                                 </div>
                                 <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                    eWay Bills
                                 </p>
                                 <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                    ₹{eWayRate} per piece
                                 </p>
                              </div>
                           </button>
                           <button
                              onClick={() => !isProcessingPayment && setCreditType('EINVOICE')}
                              disabled={isProcessingPayment}
                              className={`p-4 rounded-lg border-2 transition-all ${
                                 creditType === 'EINVOICE'
                                    ? isDark
                                       ? 'border-blue-500 bg-blue-900/20'
                                       : 'border-blue-500 bg-blue-50'
                                    : isDark
                                    ? 'border-slate-600 bg-slate-700/50 hover:border-slate-500'
                                    : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                              } ${isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''}`}>
                              <div className="text-center">
                                 <div
                                    className={`w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center mx-auto mb-2`}>
                                    <FileText className="w-4 h-4 text-white" />
                                 </div>
                                 <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                    eInvoices
                                 </p>
                                 <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                    ₹{eInvoiceRate} per piece
                                 </p>
                              </div>
                           </button>
                        </div>
                     </div>

                     {/* Credit Count Selection */}
                     <div className="mb-6">
                        <label
                           className={`block text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                           Number of Credits
                        </label>
                        <div className="flex items-center gap-3">
                           <button
                              onClick={() => !isProcessingPayment && setCreditCount(Math.max(10, creditCount - 10))}
                              disabled={isProcessingPayment}
                              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                 isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-100 hover:bg-slate-200'
                              } transition-colors ${isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''}`}>
                              <span className="text-lg">-</span>
                           </button>
                           <input
                              type="number"
                              value={creditCount}
                              onChange={(e) => !isProcessingPayment && setCreditCount(parseInt(e.target.value) || 0)}
                              min="10"
                              step="10"
                              disabled={isProcessingPayment}
                              className={`flex-1 text-center py-2 rounded-lg border ${
                                 isDark
                                    ? 'bg-slate-700 border-slate-600 text-white'
                                    : 'bg-white border-slate-200 text-slate-900'
                              } font-semibold text-lg ${isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''}`}
                           />
                           <button
                              onClick={() => !isProcessingPayment && setCreditCount(creditCount + 10)}
                              disabled={isProcessingPayment}
                              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                 isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-100 hover:bg-slate-200'
                              } transition-colors ${isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''}`}>
                              <span className="text-lg">+</span>
                           </button>
                        </div>
                        <div className="flex justify-between mt-2">
                           <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Min: 10</span>
                           <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Step: 10</span>
                        </div>
                     </div>

                     {/* Price Breakdown */}
                     <div className={`mb-6 p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                        <h4 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                           Price Breakdown
                        </h4>
                        <div className="space-y-2">
                           <div className="flex justify-between">
                              <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>Credits</span>
                              <span className={isDark ? 'text-white' : 'text-slate-900'}>{creditCount} pieces</span>
                           </div>
                           <div className="flex justify-between">
                              <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>Rate</span>
                              <span className={isDark ? 'text-white' : 'text-slate-900'}>
                                 ₹{creditType === 'EINVOICE' ? eInvoiceRate : eWayRate} per piece
                              </span>
                           </div>
                           <div className="border-t pt-2 flex justify-between">
                              <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Total</span>
                              <span className={`text-lg font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                                 ₹{calculateTotalAmount().toFixed(2)}
                              </span>
                           </div>
                        </div>
                     </div>

                     {/* Action Buttons */}
                     <div className="flex gap-3">
                        <button
                           onClick={() => setShowBuyCreditsModal(false)}
                           disabled={isProcessingPayment}
                           className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                              isDark
                                 ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                                 : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                           } ${isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''}`}>
                           Cancel
                        </button>
                        <button
                           onClick={handleBuyCredits}
                           disabled={isProcessingPayment || calculateTotalAmount() <= 0}
                           className={`flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                              isProcessingPayment || calculateTotalAmount() <= 0
                                 ? 'opacity-50 cursor-not-allowed'
                                 : 'hover:bg-green-700'
                           }`}>
                           {isProcessingPayment ? (
                              <>
                                 <RefreshCw className="w-4 h-4 animate-spin" />
                                 Processing...
                              </>
                           ) : (
                              <>
                                 <ShoppingCart className="w-4 h-4" />
                                 Buy Now
                              </>
                           )}
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         )}

         {/* Upgrade Modal - SEPARATE FIXED LAYER */}
         {showUpgrade && (
            <div className="fixed inset-0 z-[1001]">
               <PlanCarousel
                  PackageData={PackageData}
                  currentPlan={currentPlan}
                  onUpgrade={handleUpgrade}
                  onClose={() => setShowUpgrade(false)}
                  isExpired={isExpired}
                  isDark={isDark}
               />
            </div>
         )}

         {showWalletModal && (
            <WalletData
               isOpen={showWalletModal}
               onClose={() => setShowWalletModal(false)}
               walletData={walletData}
               isDark={isDark}
            />
         )}

         {/* Add CSS animations */}
         <style>{`
            @keyframes fadeIn {
               from {
                  opacity: 0;
               }
               to {
                  opacity: 1;
               }
            }

            @keyframes scaleIn {
               from {
                  opacity: 0;
                  transform: scale(0.95);
               }
               to {
                  opacity: 1;
                  transform: scale(1);
               }
            }

            .animate-fadeIn {
               animation: fadeIn 0.2s ease-out;
            }

            .animate-scaleIn {
               animation: scaleIn 0.3s ease-out;
            }
         `}</style>

         <EditBusinessModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            initialData={PackageCustomer}
            onSubmit={handleSubmit}
         />
      </div>
   );
}

export default UserDetailsPage;
